import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Star, User, MessageCircle } from 'lucide-react';
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input.tsx";
import { Textarea } from "../components/ui/textarea.tsx";
import { Card, CardContent } from "../components/ui/card.tsx";
import { useDispatch, useSelector } from 'react-redux';
import { fetchReviews } from '../rtk/slices/reviews-slice.js';
import Swal from 'sweetalert2';
// import Swal from 'sweetalert2';


const Reviews = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [name, setName] = useState('');
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [errorRating, setErrorRating] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const { reviews } = useSelector((state) => state.reviews);

  useEffect(() => {
    dispatch(fetchReviews());
  }, [dispatch]);

  // --- سيتم التعديل داخل هذه الدالة ---
  const handleSubmit = async (e) => {
    e.preventDefault();

    // --- تعديل 1: استخدام الترجمة لرسالة الخطأ ---
    if (rating < 1) {
      setErrorRating(t('reviews.validation.ratingRequired'));
      return;
    }

    setIsLoading(true);

    const reviewData = { name, rating, comment };

    try {
      const response = await fetch(`${process.env.REACT_APP_PROJECTS_API}components/submit_comment.php`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(reviewData),
      });

      const result = await response.json();

      if (response.ok) {
        // --- تعديل 2: استخدام الترجمة لرسالة النجاح ---
        Swal.fire({
          title: t('reviews.swal.successTitle'),
          text: t('reviews.swal.successText'),
          icon: 'success',
          background: '#f0f8ff',
          color: '#705540',
          iconColor: '#705540',
          timer: 3500,
          timerProgressBar: true,
          showConfirmButton: false,
        });

        dispatch(fetchReviews());
      } else {
        // --- تعديل 3: استخدام الترجمة لرسالة الخطأ من السيرفر ---
        Swal.fire({
          title: t('reviews.swal.errorTitle'),
          text: result.message, // رسالة الخطأ تأتي من الـ API، لا تحتاج لترجمة هنا
          icon: 'error',
          background: '#f0f8ff',
          color: '#FE6464',
          iconColor: '#FE6464',
          timer: 3500,
          timerProgressBar: true,
          showConfirmButton: false,
        });
      }
    } catch (error) {
      // --- تعديل 4: استخدام الترجمة لرسالة خطأ الشبكة ---
      Swal.fire({
        title: t('reviews.swal.errorTitle'),
        text: t('reviews.swal.networkErrorText'),
        icon: 'error',
        background: '#f0f8ff',
        color: '#FE6464',
        iconColor: '#FE6464',
        timer: 3000,
        timerProgressBar: true,
        showConfirmButton: false,
      });
    } finally {
      setIsLoading(false);
    }

    setName('');
    setRating(0);
    setComment('');
    setErrorRating('');
  };

  return (
    <section id="reviews" className="relative py-20 bg-white z-10">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">
            {t('reviews.title')}
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            {t('reviews.subtitle')}
          </p>
        </motion.div>

        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Reviews Display */}
          <div>
            <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-amber-600" />
              {t('reviews.customerReviews')}
            </h3>

            <div className="space-y-6 max-h-96 overflow-y-auto pr-4">
              {reviews.map((review, index) => (
                <motion.div
                  key={review.id}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Card className="border border-gray-200 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center">
                          <User className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h4 className="font-semibold text-gray-800">{review.name}</h4>
                            <div className="flex">
                              {Array.from({ length: 5 }).map((_, index) => (
                                <Star
                                  className={`w-4 h-4 transition-colors ${index < review.rating
                                    ? 'text-amber-400 fill-current'
                                    : 'text-gray-300'
                                    }`}
                                />
                              ))}
                            </div>
                          </div>
                          <p className="text-gray-600 leading-relaxed">{review.comment}</p>
                          <p className="text-sm text-gray-400 mt-2">{review.created_at}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
              {reviews.length < 1 && (
                <div className="text-center py-12 px-6 bg-amber-50/50 border border-amber-100 rounded-lg">
                  <h4 className="mt-4 text-lg font-semibold text-gray-700">{t('reviews.noReviewsFound')}</h4>
                  <p className="mt-2 text-sm text-gray-500">{t('reviews.beTheFirst')}</p>
                </div>
              )}
            </div>
          </div>

          {/* Review Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            <Card className="border-2 border-amber-200">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-gray-800 mb-6">
                  {t('reviews.writeReview')}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reviews.yourName')}
                    </label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)} placeholder={t('reviews.yourName')}
                      className="w-full"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reviews.rating')}
                    </label>
                    <div className="flex gap-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <button
                          key={i}
                          type="button"
                          onClick={() => setRating(i + 1)}
                          className="focus:outline-none"
                        >
                          <Star
                            className={`w-8 h-8 transition-colors ${i < rating
                              ? 'text-amber-400 fill-current hover:text-amber-500'
                              : 'text-gray-300 hover:text-amber-300'
                              }`}
                          />
                        </button>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {t('reviews.yourReview')}
                    </label>
                    <Textarea
                      value={comment}
                      onChange={(e) => setComment(e.target.value)}
                      placeholder={t('reviews.yourReview')}
                      rows={4}
                      className="w-full"
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    className="w-full bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white font-semibold py-3 rounded-lg transition-all duration-300 transform hover:scale-105"
                  >
                    {t('reviews.submit')}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Reviews;